import { useState, useEffect, useCallback } from 'react';
import commentService from '../services/commentService'; // Importer l'instance apiClient configurée
import i18n from '../i18n/config';
import { Comment, CommentCreate, CommentUpdate } from '../types'; // Importer aussi Create/Update

interface UseUseCaseDiscussionResult {
  comments: Comment[];
  isLoading: boolean;
  error: string | null;
  fetchComments: () => Promise<void>;
  createComment: (data: CommentCreate) => Promise<Comment | null>;
  updateComment: (commentId: string, data: CommentUpdate) => Promise<Comment | null>;
  deleteComment: (commentId: string) => Promise<boolean>;
}

export const useUseCaseDiscussion = (useCaseId: string | undefined): UseUseCaseDiscussionResult => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApiError = (err: any, context: string, fallbackKey: string): string => {
    console.error(`Error ${context}:`, err);
    const errorMsg = err.response?.data?.detail || err.message || i18n.t(fallbackKey);
    setError(errorMsg);
    return errorMsg;
  };

  const fetchComments = useCallback(async () => {
    if (!useCaseId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await commentService.listByUseCase(useCaseId);
      console.log("API Response for comments:", data); // Log the raw response
      // Ensure data is an array before setting state
      if (Array.isArray(data)) {
        setComments(data);
      } else {
        console.warn('API response for comments is not an array:', data);
        setComments([]);
      }
    } catch (err) {
      handleApiError(err, 'fetching comments', 'errors.comment.fetchFailed');
      setComments([]); // Also reset on error
    } finally {
      setIsLoading(false);
    }
  }, [useCaseId]);

  const createComment = useCallback(async (data: CommentCreate): Promise<Comment | null> => {
    if (!useCaseId) {
      setError(i18n.t('errors.useCase.missingId'));
      return null;
    }
    setIsLoading(true);
    setError(null);
    try {
      const comment = await commentService.create(useCaseId, data);
      await fetchComments(); // Recharger les commentaires après création
      return comment;
    } catch (err) {
      handleApiError(err, 'creating comment', 'errors.comment.createFailed');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [useCaseId, fetchComments]);

  const updateComment = useCallback(async (commentId: string, data: CommentUpdate): Promise<Comment | null> => {
    if (!useCaseId) {
      setError(i18n.t('errors.useCase.missingId'));
      return null;
    }
    setIsLoading(true);
    setError(null);
    try {
      // L'endpoint PUT inclut l'ID du commentaire
      const comment = await commentService.update(useCaseId, commentId, data);
      await fetchComments(); // Recharger les commentaires après mise à jour
      return comment;
    } catch (err) {
      handleApiError(err, 'updating comment', 'errors.comment.updateFailed');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [useCaseId, fetchComments]);

  const deleteComment = useCallback(async (commentId: string): Promise<boolean> => {
    if (!useCaseId) {
      setError(i18n.t('errors.useCase.missingId'));
      return false;
    }
    setIsLoading(true);
    setError(null);
    try {
      // L'endpoint DELETE inclut l'ID du commentaire
      await commentService.delete(useCaseId, commentId);
      await fetchComments(); // Recharger les commentaires après suppression
      return true;
    } catch (err) {
      handleApiError(err, 'deleting comment', 'errors.comment.deleteFailed');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [useCaseId, fetchComments]);

  // Charger les commentaires initiaux
  useEffect(() => {
    if (useCaseId) {
      fetchComments();
    } else {
      // Réinitialiser si pas d'ID
      setComments([]);
      setError(null);
    }
  }, [useCaseId, fetchComments]); // fetchComments est stable grâce à useCallback

  return {
    comments,
    isLoading,
    error,
    fetchComments,
    createComment,
    updateComment,
    deleteComment,
  };
};
